import React, { useState } from "react";
import { researchTracks } from "../data/researchTracksData";

export default function ResearchTracks() {
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [expandedPapers, setExpandedPapers] = useState({});

  const toggleTrack = (trackId) => {
    setExpandedTrack(expandedTrack === trackId ? null : trackId);
    setExpandedPapers({}); // Reset expanded papers when switching tracks
  };

  const togglePaper = (paperId) => {
    setExpandedPapers(prev => ({
      ...prev,
      [paperId]: !prev[paperId]
    }));
  };

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden" id="research-tracks">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-indigo-200/30 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/20 rounded-full blur-lg"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-slate-800 via-blue-600 to-indigo-700 bg-clip-text text-transparent mb-6">
            Research Portfolio
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore my research work across four specialized domains in artificial intelligence,
            machine learning, and intelligent systems.
          </p>
          <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Statistics Section - Moved to top */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <StatCard icon="📄" value="17" label="Total Papers" />
          <StatCard icon="🎓" value="4" label="SCI Papers" />
          <StatCard icon="📚" value="1" label="Master's Thesis" />
          <StatCard icon="🏆" value="4" label="Research Tracks" />
        </div>

        {/* Track Accordion */}
        <div className="space-y-6">
          {researchTracks.map((track) => (
            <TrackAccordion
              key={track.id}
              track={track}
              isExpanded={expandedTrack === track.id}
              onToggle={() => toggleTrack(track.id)}
              expandedPapers={expandedPapers}
              onTogglePaper={togglePaper}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrackAccordion({ track, isExpanded, onToggle, expandedPapers, onTogglePaper }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border-2 border-gray-200/50 rounded-3xl shadow-lg overflow-hidden transition-all duration-300">
      {/* Track Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-8 flex items-center gap-6 hover:bg-white/50 transition-all duration-300 group"
      >
        {/* Icon Badge */}
        <div className={`w-20 h-20 bg-gradient-to-br ${track.color} rounded-2xl flex items-center justify-center text-4xl shadow-lg flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
          {track.icon}
        </div>

        {/* Track Info */}
        <div className="flex-1 text-left">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-4 py-1 bg-gray-100 rounded-full text-sm font-semibold text-gray-700">
              Track {track.id}
            </span>
            <span className="px-4 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full text-sm font-semibold text-blue-700">
              {track.papers.length} Papers
            </span>
          </div>
          <h3 className={`text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r ${track.color} bg-clip-text text-transparent`}>
            {track.title}
          </h3>
          <p className="text-lg font-semibold text-gray-700 mb-2">
            {track.titleKo}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {track.description}
          </p>
        </div>

        {/* Expand/Collapse Icon */}
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${track.color} flex items-center justify-center text-white text-2xl transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
          ↓
        </div>
      </button>

      {/* Papers List - Expandable */}
      <div className={`transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
        <div className="px-8 pb-8 space-y-4">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6"></div>
          {track.papers.map((paper, index) => (
            <PaperItem
              key={paper.id}
              paper={paper}
              trackColor={track.color}
              index={index}
              isExpanded={expandedPapers[paper.id]}
              onToggle={() => onTogglePaper(paper.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PaperItem({ paper, trackColor, index, isExpanded, onToggle }) {
  return (
    <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
      {/* Paper Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-start gap-4 hover:bg-gray-50/50 transition-all duration-200 group"
      >
        {/* Paper Number */}
        <div className={`w-12 h-12 bg-gradient-to-br ${trackColor} rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-md flex-shrink-0`}>
          {index + 1}
        </div>

        {/* Paper Basic Info */}
        <div className="flex-1 text-left">
          <h4 className="text-lg font-bold text-gray-800 mb-1 leading-tight group-hover:text-gray-900">
            {paper.title}
          </h4>
          {paper.titleEn && (
            <p className="text-sm text-gray-600 italic mb-2">{paper.titleEn}</p>
          )}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">
              {paper.year}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-medium">
              {paper.authors}
            </span>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <div className={`w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-gray-600 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </div>
      </button>

      {/* Paper Details - Expandable */}
      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
        <div className="px-6 pb-6 space-y-4 bg-white/50">
          {/* Journal */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <p className="text-sm">
              <span className="font-semibold text-blue-700">📚 Journal:</span>{" "}
              <span className="text-gray-700">{paper.journal}</span>
            </p>
          </div>

          {/* Summary */}
          <div>
            <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <span>📝</span> Summary
            </h5>
            <p className="text-sm text-gray-700 leading-relaxed bg-blue-50/50 p-4 rounded-xl border-l-4 border-blue-400">
              {paper.summary}
            </p>
          </div>

          {/* Key Points */}
          <div>
            <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <span>💡</span> Key Points
            </h5>
            <ul className="space-y-1.5">
              {paper.keyPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={`mt-1.5 w-1.5 h-1.5 bg-gradient-to-r ${trackColor} rounded-full flex-shrink-0`}></span>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Methodology */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
            <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <span>🔬</span> Methodology
            </h5>
            <p className="text-sm text-gray-700 leading-relaxed">{paper.methodology}</p>
          </div>

          {/* Results */}
          <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-xl border border-green-200">
            <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <span>📊</span> Results
            </h5>
            <p className="text-sm text-gray-700 leading-relaxed font-medium">{paper.results}</p>
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-3 pt-2">
            {paper.doi && (
              <a
                href={paper.doi}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r ${trackColor} text-white font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
              >
                <span>📄</span>
                <span>View DOI</span>
              </a>
            )}
            {paper.link && (
              <a
                href={paper.link}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r ${trackColor} text-white font-semibold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
              >
                <span>🔗</span>
                <span>View Paper</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border-2 border-gray-200/50 p-6 rounded-2xl text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
      <div className="text-4xl mb-3">{icon}</div>
      <div className="text-3xl font-bold text-gray-800 mb-1">{value}</div>
      <div className="text-sm text-gray-600 font-medium">{label}</div>
    </div>
  );
}
